/**
 * DraftBadge — sticky badge rendered on chain pages when admin is viewing a
 * draft / in_review / needs_revision chain. Adds Lock + Send Back action
 * buttons. Only renders when chain.chain_status is admin-only.
 *
 * Methodology: drafts visible in context, marked clearly, never confused
 * with public content.
 */
import { useState } from 'react';
import { ShieldAlert, Send, RotateCcw, Loader2, X, AlertTriangle } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const ADMIN_ONLY_STATUSES = new Set(['draft', 'in_review', 'needs_revision']);

const STATUS_LABELS = {
  draft: 'DRAFT',
  in_review: 'IN REVIEW',
  needs_revision: 'NEEDS REVISION',
};

const STATUS_TONE = {
  draft: { color: '#D4A84A', bg: 'rgba(212,168,74,0.08)', border: 'rgba(212,168,74,0.4)' },
  in_review: { color: '#2F8A92', bg: 'rgba(47,138,146,0.08)', border: 'rgba(47,138,146,0.4)' },
  needs_revision: { color: '#FF6B6B', bg: 'rgba(255,107,107,0.08)', border: 'rgba(255,107,107,0.4)' },
};

export default function DraftBadge({ chain, onChainStatusChange }) {
  const { user } = useAuth();
  const [showSendBack, setShowSendBack] = useState(false);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!chain || !chain.id) return null;
  if (user?.role !== 'admin') return null;
  if (!ADMIN_ONLY_STATUSES.has(chain.chain_status)) return null;

  const tone = STATUS_TONE[chain.chain_status] || STATUS_TONE.draft;

  const onLock = async () => {
    setBusy(true);
    setError('');
    try {
      await api.post(`/admin/pipeline/${chain.id}/lock`);
      onChainStatusChange?.('locked');
    } catch (e) {
      setError(e.response?.data?.detail || 'Lock failed.');
    }
    setBusy(false);
  };

  const onSendBack = async () => {
    if (notes.trim().length < 10) { setError('Notes must be at least 10 characters.'); return; }
    setBusy(true);
    setError('');
    try {
      await api.post(`/admin/pipeline/${chain.id}/send-back`, { review_notes: notes.trim() });
      onChainStatusChange?.('needs_revision');
      setShowSendBack(false);
      setNotes('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Send back failed.');
    }
    setBusy(false);
  };

  return (
    <div
      className="fixed top-3 right-3 z-50 max-w-sm rounded shadow-lg backdrop-blur-sm"
      style={{ background: tone.bg, border: `1px solid ${tone.border}` }}
      data-testid={`draft-badge-${chain.chain_status}`}
    >
      <div className="px-3 py-2 flex items-center gap-2 border-b" style={{ borderColor: tone.border }}>
        <ShieldAlert size={14} style={{ color: tone.color }} />
        <span className="font-hud text-[10px] tracking-widest uppercase" style={{ color: tone.color }}>
          {STATUS_LABELS[chain.chain_status]}
        </span>
        <span className="font-mono text-[10px] text-white/65 ml-auto truncate max-w-[180px]">
          admin · pipeline review
        </span>
      </div>

      <div className="p-3">
        {chain.chain_status === 'needs_revision' && chain.review_notes && (
          <div className="mb-2 p-2 rounded border border-[#FF6B6B]/30 bg-[#FF6B6B]/05">
            <div className="font-hud text-[9px] tracking-widest text-[#FF6B6B] mb-1">YOUR NOTES</div>
            <div className="font-mono text-[11px] text-white/85 leading-relaxed">{chain.review_notes}</div>
          </div>
        )}

        {!showSendBack ? (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={onLock}
              disabled={busy}
              data-testid="draft-lock-btn"
              className="font-hud text-[10px] tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-3 py-1.5 hover:bg-[#D4A84A]/85 disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {busy ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
              LOCK
            </button>
            <button
              onClick={() => setShowSendBack(true)}
              disabled={busy}
              data-testid="draft-send-back-btn"
              className="font-hud text-[10px] tracking-widest text-white/85 border border-[#FF6B6B]/40 px-3 py-1.5 hover:text-[#FF6B6B] inline-flex items-center gap-1.5"
            >
              <RotateCcw size={11} /> SEND BACK
            </button>
          </div>
        ) : (
          <div data-testid="draft-send-back-form">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Review notes (≥10 chars). Sent to the orchestrator on submit…"
              rows={3}
              data-testid="draft-send-back-notes"
              className="w-full bg-[#0A0A0A] border border-[#FF6B6B]/30 rounded px-2 py-1.5 font-mono text-[11px] text-white/85 outline-none focus:border-[#FF6B6B]/55"
            />
            <div className="flex gap-2 mt-2">
              <button
                onClick={onSendBack}
                disabled={busy}
                data-testid="draft-send-back-submit"
                className="font-hud text-[10px] tracking-widest text-[#0A0A0A] bg-[#FF6B6B] px-3 py-1.5 hover:bg-[#FF6B6B]/85 disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {busy ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />}
                CONFIRM SEND BACK
              </button>
              <button
                onClick={() => { setShowSendBack(false); setNotes(''); setError(''); }}
                className="font-hud text-[10px] tracking-widest text-white/65 px-2 py-1.5 hover:text-white"
              >
                <X size={11} className="inline" /> CANCEL
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-2 flex items-start gap-1.5 font-mono text-[10px] text-[#FF6B6B]" data-testid="draft-error">
            <AlertTriangle size={10} className="mt-0.5 flex-shrink-0" />
            <span>{typeof error === 'string' ? error : JSON.stringify(error)}</span>
          </div>
        )}
      </div>
    </div>
  );
}
